import { Box, Checkbox, Grid, Pagination, Stack, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppTransComponents } from 'app/appTrans';
import { MprcConfirmDialog, MprcIconButtonMoreOptionsDropdown, MprcRequestErrorDialog, MprcRequestIndicator, MprcText } from 'components';
import { MprcDropdownBuildGroupedItems, MprcDropdownItems, MprcIconButtonDropdown } from "components/MprcIconButtonDropdown";
import { IconEllipsisV } from 'components/icons';
import { DirectMessageListResponse, DirectMessageListResponsePaginatedList, SelectMessageDirect } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { ViewDirectMessage } from 'views/foundation/directMessage/ViewDirectMessage';

type MenuOptions = "markRead" | "markNoRead" | "remove";

interface IProps {
    messageListType: SelectMessageDirect;
    onReply: (message: DirectMessageListResponse) => void;
    onForward: (message: DirectMessageListResponse) => void;
}

export const ListDirectMessage: React.FC<IProps> = (props) => {

    const { messageListType, onReply, onForward } = props;

    const api = useCoreApi();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<DirectMessageListResponsePaginatedList>();
    const [loading, setLoading] = useState<boolean>(false);
    const [posting, setPosting] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [selectedMessage, setSelectedMessage] = useState<DirectMessageListResponse | null>(null);
    const [dialog, setDialog] = useState<any>();
    const [idHovering, setIdHovering] = useState<string | null>(null);

    ///
    async function deleteMessage(messageIds: string[]) {
        try {
            setPosting(true);
            const result = await api.v1.directMessage.deleteDirectMessage({ messageIds });
            if (result.data) {
                enqueueSnackbar(t("DirectMessage.List.DeleteSuccessMessage"), { variant: "success" });
                if (viewData?.items.length == messageIds.length) {
                    setPageNumber(1);
                }
                setLoading(true);
                setSelectedMessage(null);
                setSelectedItems([]);
            }
        } catch (e) {
            setViewError(e);
        } finally {
            setPosting(false);
        }
    }

    ///
    async function markRead(messageIds: string[], markRead: boolean) {
        try {
            setPosting(true);
            const result = await api.v1.directMessage.markReadDirectMessage({ messageIds, markRead });
            if (result.data) {
                if (!markRead) setSelectedMessage(null);
                setSelectedItems([]);
                setLoading(true);
            }
        } catch (e) {
            setViewError(e);
        } finally {
            setPosting(false);
        }
    }

    ///
    const getMenuItems = useCallback(
        (item?: DirectMessageListResponse): MprcDropdownItems<MenuOptions> => {
            const groups: MprcDropdownItems<MenuOptions>[] = [];

            const group0: MprcDropdownItems<MenuOptions> = [];

            if (messageListType == SelectMessageDirect.Received) {
                if (item == undefined || !item.read)
                    group0.push({ text: <MprcText color="muted">{t("DirectMessage.List.Actions.MarkRead")}</MprcText>, value: "markRead" });
                if (item == undefined || item.read)
                    group0.push({ text: <MprcText color="muted">{t("DirectMessage.List.Actions.MarkNoRead")}</MprcText>, value: "markNoRead" });
                groups.push(group0);
            }

            const group1: MprcDropdownItems<MenuOptions> = [
                { text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" },
            ];
            groups.push(group1);

            return MprcDropdownBuildGroupedItems<MenuOptions>(groups);
        },
        [viewData]
    );

    ///
    const handleSelectOption = (option: MenuOptions) => {
        switch (option) {
            case "markNoRead":
            case "markRead":
                markRead(selectedItems, option == "markRead");
                break;
            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>
                        <Trans
                            i18nKey="DirectMessage.List.ConfirmDeleteSelectedMessage"
                            components={AppTransComponents}
                        />
                    </span>}
                    onClose={
                        (result) => {
                            if (result) {
                                deleteMessage(selectedItems);
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;
        }
    };

    ///
    const handleSelectOptionItem = (option: MenuOptions, item: DirectMessageListResponse) => {
        switch (option) {
            case "markNoRead":
            case "markRead":
                markRead([item.messageId], option == "markRead");
                break;
            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>
                        <Trans
                            i18nKey="DirectMessage.List.ConfirmDeleteMessage"
                            components={AppTransComponents}
                        />
                    </span>}
                    onClose={
                        (result) => {
                            if (result) {
                                deleteMessage([item.messageId]);
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    /// Ao carregar os items
    useEffect(() => {
        async function getData() {
            try {
                const result = await api.v1.directMessage.getDirectMessages({
                    pageNumber,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    selectMessage: messageListType
                });
                setViewData(result.data);
            } catch (e) {
                setViewData(undefined);
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    /// Ao carregar os items
    useEffect(() => {
        setPageNumber(1);
        setSelectedItems([]);
        setSelectedMessage(null);
        setLoading(true);
    }, [messageListType]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator visible={loading || posting} type={loading ? "request" : "post"} />

            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            {dialog}

            <Stack direction="column" width="100%" height="100%">

                <Box flexGrow={1} width="100%" height="100%" overflow="auto">
                    {
                        viewData && (
                            <Stack direction="column" height="100%" width="100%">
                                <Box>
                                    <Grid container alignItems="center" sx={{ borderBottom: 1 }} spacing={1}>
                                        <Grid alignSelf="flex-start" width={"40px"} textAlign="center">
                                            <Checkbox
                                                checked={selectedItems.length == viewData.items.length}
                                                onChange={(e) => setSelectedItems(e.target.checked ? viewData.items.map(x => x.messageId) : [])}
                                                sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 2 }}>
                                            <Typography variant="overline" color="black" textTransform="uppercase">
                                                {t("DirectMessage.List.DateText")}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography variant="overline" color="black" textTransform="uppercase">
                                                {t(messageListType == SelectMessageDirect.Received ? "DirectMessage.List.FromText" : "DirectMessage.List.ToText")}
                                            </Typography>
                                        </Grid>
                                        <Grid size="grow">
                                            <Typography variant="overline" color="black" textTransform="uppercase">
                                                {t("DirectMessage.List.SubjectText")}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 0.7 }}>
                                            {
                                                selectedItems.length > 0 &&
                                                <MprcIconButtonMoreOptionsDropdown
                                                    color="muted"
                                                    onChange={(selection) => handleSelectOption(selection)}
                                                    items={getMenuItems()}
                                                />
                                            }
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box flexGrow={1} overflow="auto">
                                    <Grid container direction="column" mt={1} spacing={0}>
                                        {
                                            viewData.items.map((item) => {
                                                const isSelected = selectedMessage?.messageId == item.messageId;
                                                const highlight = !item.read;
                                                return (
                                                    <Grid key={item.messageId}
                                                        sx={(theme) => ({
                                                            backgroundColor: isSelected ? theme.palette.paper.main : "inherit",
                                                            borderRadius: "8px",
                                                            maxHeight: "38px",
                                                            "&:hover": {
                                                                backgroundColor: theme.palette.paper.main,
                                                                cursor: "pointer"
                                                            }
                                                        })}
                                                    >
                                                        <div
                                                            onClick={() => setSelectedMessage(selectedMessage?.messageId == item.messageId ? null : item)}
                                                            onMouseOver={() => setIdHovering(item.messageId)}
                                                            onMouseOut={() => setIdHovering(null)}
                                                        >
                                                            <Grid container alignContent="start" alignItems="center" maxWidth="710px" spacing={1}>
                                                                <Grid width={"40px"}>
                                                                    <Checkbox
                                                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                                                        checked={selectedItems.some(x => x == item.messageId)}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                if (!selectedItems.some(x => x = item.messageId))
                                                                                    setSelectedItems((state) => { return [...state, item.messageId]; });
                                                                            } else {
                                                                                setSelectedItems((state) => { return state.filter(x => x != item.messageId); });
                                                                            }
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid size={{ xs: 2 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color={highlight ? "primary" : "inherit"}
                                                                        component="div"
                                                                        noWrap
                                                                        overflow="clip"
                                                                        fontWeight={highlight ? "bold" : "inherit"}
                                                                    >
                                                                        {Formaters.performaDate(item.createdOn, { preposition: "none" })}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: 3 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color={highlight ? "primary" : "inherit"}
                                                                        component="div"
                                                                        noWrap
                                                                        overflow="clip"
                                                                        fontWeight={highlight ? "bold" : "inherit"}
                                                                    >
                                                                        {messageListType == SelectMessageDirect.Received ? item.sender.name : item.recipient.name}
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid size={{ xs: idHovering == item.messageId ? 5.7 : 6 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color={highlight ? "primary" : "inherit"}
                                                                        component="div"
                                                                        noWrap
                                                                        overflow="clip"
                                                                        fontWeight={highlight ? "bold" : "inherit"}
                                                                    >
                                                                        {item.subject}
                                                                    </Typography>
                                                                </Grid>
                                                                {
                                                                    (idHovering == item.messageId) &&
                                                                    <Grid size={{ xs: 0.3 }}>
                                                                        <MprcIconButtonDropdown
                                                                            icon={<IconEllipsisV fontSize="14px" width="14px" />}
                                                                            color="muted"
                                                                            separator="blank"
                                                                            onChange={(selection) => handleSelectOptionItem(selection, item)}
                                                                            items={getMenuItems(item)}
                                                                        />
                                                                    </Grid>
                                                                }
                                                            </Grid>
                                                        </div>
                                                    </Grid>
                                                );
                                            })}
                                    </Grid>
                                </Box>
                                <Box>
                                    {
                                        !loading && viewData.totalPages > 1 &&
                                        <Stack alignContent="center" alignItems="center" width="100%" marginY={0.5}>
                                            <Pagination
                                                size="small"
                                                shape="rounded"
                                                color="primary"
                                                page={pageNumber}
                                                count={viewData.totalPages}
                                                onChange={
                                                    (e, pageNumber) => {
                                                        if (pageNumber != viewData.pageNumber) {
                                                            setPageNumber(pageNumber);
                                                            setLoading(true);
                                                        }
                                                    }
                                                }
                                            />
                                        </Stack>
                                    }
                                </Box>
                            </Stack>
                        )
                    }
                </Box>

                <Box alignSelf="end" width="100%" >
                    {
                        selectedMessage &&
                        <ViewDirectMessage
                            canReply={messageListType == SelectMessageDirect.Received}
                            message={selectedMessage}
                            onDelete={() => handleSelectOptionItem("remove", selectedMessage)}
                            onReply={() => onReply(selectedMessage)}
                            onForward={() => onForward(selectedMessage)}
                            onRead={() => markRead([selectedMessage.messageId], true)}
                        />
                    }
                </Box>
            </Stack>
        </>
    );
};
